import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  alpha,
  IconButton,
  Grid,
  CircularProgress,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Close,
  Person,
  Email,
  Phone,
  Business,
  Send,
} from '@mui/icons-material';
import { createLead } from '../../services/request/LoanLeads';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AmountInput from '../../shared-components/amount-input/AmountInput';
import { useTranslation } from '../../i18n';

interface LeadCaptureModalProps {
  open: boolean;
  onClose: () => void;
  initialLoanAmount?: number;
  initialInterestRate?: number;
  initialTenureMonths?: number;
}

const LeadCaptureModal: React.FC<LeadCaptureModalProps> = ({
  open,
  onClose,
  initialLoanAmount = 500000,
  initialInterestRate = 2,
  initialTenureMonths = 12,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [isHuman, setIsHuman] = useState(false); // I am not a robot checkbox
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    businessName: '',
    requestedAmount: initialLoanAmount,
    interest: initialInterestRate,
    duration: initialTenureMonths,
    addressLine1: '',
    city: '',
    state: '',
    pincode: '',
    loanType: 'LOAN_ON_INTEREST',
  });

  // Update formData when initial values change (when calculator values change)
  React.useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      requestedAmount: initialLoanAmount,
      interest: initialInterestRate,
      duration: initialTenureMonths,
    }));
  }, [initialLoanAmount, initialInterestRate, initialTenureMonths]);

  // Reset form when modal closes
  React.useEffect(() => {
    if (!open) {
      setIsHuman(false);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        businessName: '',
        requestedAmount: initialLoanAmount,
        interest: initialInterestRate,
        duration: initialTenureMonths,
        addressLine1: '',
        city: '',
        state: '',
        pincode: '',
        loanType: 'LOAN_ON_INTEREST',
      });
    }
  }, [open, initialLoanAmount, initialInterestRate, initialTenureMonths]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'requestedAmount' || name === 'interest' || name === 'duration'
        ? Number(value)
        : value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate "I am not a robot" checkbox
    if (!isHuman) {
      toast.error(t('home.lead.verifyHuman'), {
        position: 'top-center',
      });
      return;
    }
    
    setLoading(true);

    try {
      // Prepare lead data with required fields
      const leadData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        addressLine1: formData.addressLine1 || `${formData.businessName}`,
        addressLine2: formData.businessName,
        city: formData.city || '',
        state: formData.state || '',
        pincode: formData.pincode || '',
        requestedAmount: formData.requestedAmount,
        interest: formData.interest,
        duration: formData.duration,
        loanType: formData.loanType,
        startDate: new Date().toISOString().split('T')[0],
      };

      const result = await createLead(leadData);

      if (result.status === 200) {
        toast.success(t('home.lead.success'), {
          position: 'top-center',
        });
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          businessName: '',
          requestedAmount: initialLoanAmount,
          interest: initialInterestRate,
          duration: initialTenureMonths,
          addressLine1: '',
          city: '',
          state: '',
          pincode: '',
          loanType: 'LOAN_ON_INTEREST',
        });
        setIsHuman(false); // Reset robot check
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        toast.error(t('home.lead.genericError'), {
          position: 'top-center',
        });
      }
    } catch (error) {
      console.error('Error creating lead:', error);
      toast.error(t('home.lead.submitError'), {
        position: 'top-center',
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <>
      <ToastContainer />
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: '#ffffff',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #101828 0%, #1D2939 100%)',
            color: 'white',
            py: 3,
            position: 'relative',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Business sx={{ fontSize: 32 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                {t('home.lead.title')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {t('home.lead.subtitle')}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              sx={{
                color: 'white',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 4, background: '#F9F8F5' }}>
          <form onSubmit={handleSubmit} id="lead-capture-form">
            {/* Loan Summary Card */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, rgba(198, 167, 106, 0.1) 0%, rgba(198, 167, 106, 0.1) 100%)',
                borderRadius: 2,
                p: 2.5,
                mb: 3,
                border: '1px solid rgba(198, 167, 106, 0.2)',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#101828' }}>
                {t('home.lead.summaryTitle')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: alpha('#101828', 0.6), display: 'block', mb: 0.5 }}>
                    {t('home.lead.amount')}
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#C6A76A', fontWeight: 700 }}>
                    {formatCurrency(formData.requestedAmount)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: alpha('#101828', 0.6), display: 'block', mb: 0.5 }}>
                    {t('home.lead.rate')}
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#D9C08A', fontWeight: 700 }}>
                    {t('home.lead.ratePerMonth', { value: formData.interest })}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: alpha('#101828', 0.6), display: 'block', mb: 0.5 }}>
                    {t('home.lead.tenure')}
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#C6A76A', fontWeight: 700 }}>
                    {t('home.lead.tenureMonths', { count: formData.duration })}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: alpha('#101828', 0.6), display: 'block', mb: 0.5 }}>
                    {t('home.lead.monthlyInterest')}
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#D9C08A', fontWeight: 700 }}>
                    {formatCurrency((formData.requestedAmount * formData.interest) / 100)}
                  </Typography>
                </Grid>
              </Grid>
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(198, 167, 106, 0.2)' }}>
                <Typography variant="caption" sx={{ color: alpha('#101828', 0.7), display: 'block', mb: 0.5 }}>
                  {t('home.lead.totalPayable')}
                </Typography>
                <Typography variant="h5" sx={{ color: '#101828', fontWeight: 800 }}>
                  {formatCurrency(formData.requestedAmount + (formData.duration * (formData.requestedAmount * formData.interest) / 100))}
                </Typography>
                <Typography variant="caption" sx={{ color: alpha('#101828', 0.6), display: 'block', mt: 0.5 }}>
                  {t('home.lead.breakdown', {
                    principal: formatCurrency(formData.requestedAmount),
                    interest: formatCurrency((formData.duration * (formData.requestedAmount * formData.interest) / 100)),
                  })}
                </Typography>
              </Box>
            </Box>

            {/* Form Fields */}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('home.lead.firstName')}
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: <Person sx={{ mr: 1, color: alpha('#101828', 0.5) }} />,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'white',
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('home.lead.lastName')}
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: <Person sx={{ mr: 1, color: alpha('#101828', 0.5) }} />,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'white',
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('home.lead.email')}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: <Email sx={{ mr: 1, color: alpha('#101828', 0.5) }} />,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'white',
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('home.lead.phone')}
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: <Phone sx={{ mr: 1, color: alpha('#101828', 0.5) }} />,
                  }}
                  inputProps={{
                    maxLength: 10,
                    pattern: '[0-9]*',
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'white',
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('home.lead.businessName')}
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: <Business sx={{ mr: 1, color: alpha('#101828', 0.5) }} />,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'white',
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('home.lead.city')}
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'white',
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('home.lead.state')}
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'white',
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <AmountInput
                  fullWidth
                  label={t('home.lead.amount')}
                  name="requestedAmount"
                  value={formData.requestedAmount}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ fontWeight: 600, color: '#101828' }}>₹</Typography>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    min: 100000,
                    max: 1000000,
                    step: 50000,
                  }}
                  helperText={t('home.lead.amountHelper')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'white',
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* I am not a robot checkbox */}
            <Box
              sx={{
                mt: 3,
                p: 2.5,
                background: 'white',
                borderRadius: 2,
                border: `2px solid ${isHuman ? '#C6A76A' : alpha('#101828', 0.2)}`,
                transition: 'all 0.3s ease',
                boxShadow: isHuman ? '0 4px 12px rgba(198, 167, 106, 0.2)' : 'none',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isHuman}
                    onChange={(e) => setIsHuman(e.target.checked)}
                    sx={{
                      color: '#101828',
                      '&.Mui-checked': {
                        color: '#C6A76A',
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: isHuman ? 700 : 500,
                        color: isHuman ? '#C6A76A' : '#101828',
                      }}
                    >
                      {t('home.lead.notRobot')}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: alpha('#101828', 0.6),
                        ml: 1,
                      }}
                    >
                      {t('home.lead.required')}
                    </Typography>
                  </Box>
                }
                sx={{ m: 0 }}
              />
            </Box>

            <Box sx={{ mt: 3, p: 2, background: 'rgba(16, 24, 40, 0.1)', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ color: alpha('#101828', 0.7), textAlign: 'center' }}>
                {t('home.lead.privacy')}
              </Typography>
            </Box>
          </form>
        </DialogContent>

        <DialogActions sx={{ p: 3, background: '#F9F8F5', borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            form="lead-capture-form"
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
            disabled={loading || !isHuman}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: 'linear-gradient(135deg, #101828 0%, #1D2939 100%)',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1rem',
              boxShadow: '0 4px 15px rgba(16, 24, 40, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1D2939 0%, #101828 100%)',
                boxShadow: '0 6px 20px rgba(16, 24, 40, 0.4)',
              },
            }}
          >
            {loading ? t('home.lead.submitting') : t('home.lead.submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LeadCaptureModal;
